/**
 * Platform adapter — lets `@slumbr/core` run on web, Electron, and React
 * Native without bundling browser globals directly.
 *
 * Apps call `configurePlatform()` once at startup. If they don't,
 * non-storage adapters degrade to safe no-ops / fallbacks so module
 * load can't crash; storage access throws loudly because auth + API
 * client cannot function without it.
 *
 * Storage is intentionally synchronous: the auth + API code paths assume
 * sync reads. Web backs this with localStorage; React Native should back
 * it with a sync KV (react-native-mmkv) — not AsyncStorage.
 */

/** Sync key/value storage. Same shape as the localStorage subset we use. */
export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface PlatformAdapters {
  storage: StorageAdapter;
  /** Read a build-time env var (e.g. VITE_API_URL). Falls back when missing. */
  getEnvVar: (key: string, fallback: string) => string;
  /** Invoked when a request returns 401 after we've cleared local tokens. */
  onUnauthorized: () => void;
  /** Whether the host OS reports a dark-mode preference. */
  prefersDark: () => boolean;
}

let adapters: Partial<PlatformAdapters> = {};

/**
 * Wire host-platform adapters. Call once at app startup, before any
 * `@slumbr/core` auth/api/store code runs. Repeat calls merge — later
 * keys win — so tests can override individual adapters.
 */
export function configurePlatform(next: Partial<PlatformAdapters>): void {
  adapters = { ...adapters, ...next };
}

/** Test-only: drop all configured adapters. */
export function resetPlatform(): void {
  adapters = {};
}

export function getStorage(): StorageAdapter {
  if (adapters.storage) return adapters.storage;
  throw new Error(
    "[@slumbr/core] Storage adapter not configured. Call configurePlatform({ storage }) before using auth / API."
  );
}

export function getEnvVar(key: string, fallback: string): string {
  return adapters.getEnvVar ? adapters.getEnvVar(key, fallback) : fallback;
}

export function triggerUnauthorized(): void {
  adapters.onUnauthorized?.();
}

export function prefersDark(): boolean {
  return adapters.prefersDark ? adapters.prefersDark() : false;
}
